import { useMemo } from 'react';
import { OverlayScrollArea } from '@odo/shared-ui';
import { RANGE_OPTIONS, useBillingRange } from '../../hooks/useBillingRange';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useMonitoringOverview } from '../../hooks/useMonitoringOverview';
import { useMonitoringOverviewHistory } from '../../hooks/useMonitoringOverviewHistory';
import { MonitoringOverviewContent } from '../../components/monitoring/overview/MonitoringOverviewContent';
import { OverviewHeaderSync } from '../../components/monitoring/overview/OverviewHeaderSync';
import {
    periodForRange,
    type HistoryView
} from '../../components/monitoring/overview/overviewPeriod';
import { OverviewRangeBar } from '../../components/monitoring/overview/OverviewRangeBar';

const RANGE_STORAGE_KEY = 'monitoring_overview_range';

function useHistoryView() {
    const { range, setRange, rangeLabel } = useBillingRange(RANGE_STORAGE_KEY, RANGE_OPTIONS, '7D');
    const { history, isSwitching, isError, retry } = useMonitoringOverviewHistory(range);
    // Labels follow the range the data is for, so the previous range's cards never wear the new name.
    const view = useMemo<HistoryView>(
        () => ({
            data: history,
            failed: isError && history === null,
            period: periodForRange(history?.range ?? range)
        }),
        [history, isError, range]
    );
    return { range, setRange, rangeLabel, view, isSwitching, isError, retry };
}

export function MonitoringOverview() {
    usePageTitle('Monitoring · Overview');
    const { overview, isFetching, isError, retry } = useMonitoringOverview();
    const historyState = useHistoryView();

    return (
        <OverlayScrollArea className="h-full bg-gray-100" contentClassName="px-8 pt-2 pb-8">
            <OverviewHeaderSync
                generatedAt={overview?.generatedAt ?? null}
                refreshFailed={isError && overview !== null}
            />
            <div className="flex flex-col gap-4">
                <OverviewRangeBar
                    range={historyState.range}
                    onRangeChange={historyState.setRange}
                    rangeLabel={historyState.rangeLabel}
                    archive={historyState.view.data?.archive ?? null}
                    switching={historyState.isSwitching}
                    failed={historyState.isError}
                    onRetry={() => void historyState.retry()}
                />
                <MonitoringOverviewContent
                    overview={overview}
                    liveFailed={isError && !isFetching}
                    onRetryLive={() => void retry()}
                    history={historyState.view}
                />
            </div>
        </OverlayScrollArea>
    );
}
